import {
  hasProvider,
  listProviders,
  onProviderRegistered,
} from "@microsoft/voice-widget-core-client";
import { mountVoiceAgent } from "@microsoft/voice-widget-ui";
import type { MountVoiceAgentOptions, VoiceAgentController } from "@microsoft/voice-widget-ui";
import {
  parseAccentColorAttribute,
  parseAttributes,
  parseLangStringsAttribute,
  parseOrbPaletteAttribute,
  parseThemeAttribute,
  parseUiLocaleAttribute,
} from "./attributes";
import type { VoiceAgentEventMap } from "./events.js";

/** The custom element tag registered by {@link register}. */
export const TAG_NAME = "voice-agent";

/**
 * The `<voice-agent>` custom element. On connect it parses its attributes and
 * mounts the widget — unless `provider` names a provider that has not
 * self-registered yet, in which case it waits and mounts as soon as that
 * provider registers (reporting a `provider_not_registered` error if none has
 * after 10s). On disconnect it tears the controller down (or, if still waiting,
 * cancels the wait). Connection attributes are mount-once; theme, accent, and
 * language strings update live.
 */
export class VoiceAgentElement extends HTMLElement {
  #controller: VoiceAgentController | undefined;
  #awaitingProvider: (() => void) | undefined;
  #awaitingProviderTimer: ReturnType<typeof setTimeout> | undefined;
  // True once the wait deadline has reported itself through `data-voice-agent-error`.
  // Scoped precisely so #stopAwaitingProvider() clears only the deadline's own
  // attribute and never one written by a failed #mount().
  #awaitingProviderReported = false;

  static get observedAttributes(): string[] {
    return ["theme", "accent-color", "orb-palette", "lang-strings", "ui-locale"];
  }

  declare addEventListener: (<K extends keyof VoiceAgentEventMap>(
    type: K,
    listener: (this: VoiceAgentElement, ev: VoiceAgentEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ) => void) &
    (<K extends keyof HTMLElementEventMap>(
      type: K,
      listener: (this: VoiceAgentElement, ev: HTMLElementEventMap[K]) => void,
      options?: boolean | AddEventListenerOptions,
    ) => void) &
    ((
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ) => void);

  declare removeEventListener: (<K extends keyof VoiceAgentEventMap>(
    type: K,
    listener: (this: VoiceAgentElement, ev: VoiceAgentEventMap[K]) => void,
    options?: boolean | EventListenerOptions,
  ) => void) &
    (<K extends keyof HTMLElementEventMap>(
      type: K,
      listener: (this: VoiceAgentElement, ev: HTMLElementEventMap[K]) => void,
      options?: boolean | EventListenerOptions,
    ) => void) &
    ((
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | EventListenerOptions,
    ) => void);

  connectedCallback(): void {
    // Guard against a re-entrant connect (a duplicate/spurious connectedCallback
    // without an intervening disconnectedCallback) overwriting — and leaking —
    // the live controller (provider connection, shell, media), or stacking a
    // second registry subscription on top of a pending one.
    if (this.#controller || this.#awaitingProvider) return;
    const provider = this.getAttribute("provider");
    // A provider may self-register from a separate <script> that has not run
    // yet; async script order is not guaranteed. Waiting is strictly better
    // than erroring, because the embedder cannot control that order. A missing
    // `provider` attribute is a real misconfiguration and still falls through
    // to #mount(), where parseAttributes reports it.
    if (provider && !hasProvider(provider)) {
      // Observable from devtools/tests while the wait is silent otherwise (see the
      // timer below); cleared in #stopAwaitingProvider() on mount or disconnect.
      this.setAttribute("data-voice-agent-awaiting-provider", provider);
      // A late-registering provider is expected and should stay silent. But a
      // *misspelled* provider name looks identical from here — it also just never
      // registers — and would otherwise wait forever with nothing observable, the
      // exact failure mode #mount()'s error handling exists to prevent. 10s is long
      // enough that a slow async provider script does not trip this warning. At the
      // deadline the wait reports itself on the same three channels a mount failure
      // uses, because a host page's monitoring is wired to `voice-agent:error`, not
      // to the console: a never-registering provider must not fail silently there.
      this.#awaitingProviderTimer = setTimeout(() => {
        const registered = listProviders();
        const message =
          `still waiting for provider "${provider}" to register after 10s ` +
          `(registered providers: ${registered.length > 0 ? registered.join(", ") : "none"}). ` +
          "The provider's script may not have loaded, or the provider name may be misspelled.";
        // Deliberately NOT `mount_failed`, which means a mount was attempted and
        // threw. Nothing has been attempted here, so a host distinguishing the two
        // can retry/register rather than treat the embed as misconfigured.
        this.setAttribute("data-voice-agent-error", message);
        // Latched before dispatch, not after: a host handler for this very event
        // may synchronously remove the element, and disconnectedCallback's cleanup
        // must already know the attribute is the deadline's to clear.
        // `data-voice-agent-awaiting-provider` deliberately stays set: the two
        // attributes describe different things — one that the element is still
        // waiting, the other that the wait has now gone on long enough to report.
        this.#awaitingProviderReported = true;
        this.dispatchEvent(
          new CustomEvent("voice-agent:error", {
            // `recoverable: true`: the provider may still register (a very slow
            // script), and the element is still waiting — it mounts if it arrives.
            detail: { code: "provider_not_registered", message, recoverable: true },
            bubbles: true,
            composed: true,
          }),
        );
        console.warn(`[voice-agent] ${message}`);
      }, 10_000);
      this.#awaitingProvider = onProviderRegistered((name) => {
        if (name !== provider) return;
        // The copied listener array (see registerProvider) still calls listeners
        // that unsubscribed mid-notification, e.g. an element another listener's
        // #mount() caused to be removed from the DOM. Without this guard that
        // element would mount detached, leaking its provider connection and shell.
        // `#controller` must also be checked: a synchronous remove-then-re-append
        // (e.g. a host handler re-parenting this element in reaction to another
        // element's voice-agent:call) re-fires connectedCallback before this stale
        // listener gets its turn. That reconnect already saw the now-registered
        // provider and mounted for real, so without this half of the guard this
        // listener would call #mount() a second time and overwrite (leak) it.
        if (!this.isConnected || this.#controller) return;
        this.#stopAwaitingProvider();
        this.#mount();
      });
      return;
    }
    this.#mount();
  }

  /**
   * Leave the waiting state, from either exit path — the provider arrived (mount
   * follows) or the element was disconnected. Clears everything the wait put in
   * place: the subscription, the deadline timer, and both attributes it may have
   * set. `data-voice-agent-error` is removed only when *this* wait wrote it, so a
   * message left by a failed #mount() survives a later disconnect.
   */
  #stopAwaitingProvider(): void {
    this.#awaitingProvider?.();
    this.#awaitingProvider = undefined;
    if (this.#awaitingProviderTimer !== undefined) {
      clearTimeout(this.#awaitingProviderTimer);
      this.#awaitingProviderTimer = undefined;
    }
    this.removeAttribute("data-voice-agent-awaiting-provider");
    if (this.#awaitingProviderReported) {
      this.removeAttribute("data-voice-agent-error");
      this.#awaitingProviderReported = false;
    }
  }

  #mount(): void {
    try {
      const emit = <K extends keyof VoiceAgentEventMap>(
        type: K,
        detail: VoiceAgentEventMap[K]["detail"],
      ): void => {
        this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
      };
      // Functions can't be attributes, so give the host a pre-connect hook
      // to register clientTools (+ per-call overrides) by mutating detail.config.
      const injected: Pick<MountVoiceAgentOptions, "clientTools"> = {};
      this.dispatchEvent(
        new CustomEvent("voice-agent:call", {
          detail: { config: injected },
          bubbles: true,
          composed: true,
        }),
      );
      this.#controller = mountVoiceAgent(this, {
        ...parseAttributes(this),
        ...(injected.clientTools ? { clientTools: injected.clientTools } : {}),
        onStatusChange: (s) => emit("voice-agent:statuschange", s),
        onModeChange: (m) => emit("voice-agent:mode", m),
        onTranscript: (m) => emit("voice-agent:transcript", m),
        onError: (e) => emit("voice-agent:error", e),
        onConnect: (info) => emit("voice-agent:connect", info),
        onDisconnect: (info) => emit("voice-agent:disconnect", info),
        onMuted: (muted) => emit("voice-agent:muted", muted),
        onExpand: () => emit("voice-agent:expand", null),
        onCollapse: () => emit("voice-agent:collapse", null),
        onUnhandledClientToolCall: (call) => emit("voice-agent:unhandledtool", call),
        onTelemetryEvent: (e) => emit("voice-agent:telemetry", e),
      });
      // parseAttributes() does not read `ui-locale`; apply an initial value from
      // the markup here so a declaratively-set locale takes effect on first mount
      // (observedAttributes only fires on later changes).
      if (this.hasAttribute("ui-locale")) {
        this.#controller.setUiLocale(parseUiLocaleAttribute(this.getAttribute("ui-locale")));
      }
    } catch (e) {
      // A mount failure is almost always a misconfigured attribute, and it is
      // the first thing a new embedder hits. Failing to console alone made that
      // hard to diagnose: nothing renders and nothing is observable from script.
      // So surface it three ways, without injecting visible text into the host's
      // page (a broken widget must not deface a production site):
      //   - `voice-agent:error`, the same channel session errors already use, so
      //     existing host error handling catches setup failures too;
      //   - a `data-voice-agent-error` attribute, so the failure is visible in
      //     devtools and assertable from a test;
      //   - the console message, kept for the case where nothing is listening.
      const message = e instanceof Error ? e.message : String(e);
      this.setAttribute("data-voice-agent-error", message);
      this.dispatchEvent(
        new CustomEvent("voice-agent:error", {
          detail: { code: "mount_failed", message, recoverable: false },
          bubbles: true,
          composed: true,
        }),
      );
      console.error(`[voice-agent] failed to mount: ${message}`);
    }
  }

  disconnectedCallback(): void {
    this.#stopAwaitingProvider();
    this.#controller?.destroy();
    this.#controller = undefined;
  }

  /**
   * Imperative UI-locale switch for hosts (e.g. Foundry Portal) driving locale
   * from their own i18n runtime. Preferred over the `ui-locale` attribute for
   * frequent in-call updates. No-op before the element has mounted.
   */
  setUiLocale(locale: Parameters<VoiceAgentController["setUiLocale"]>[0]): void {
    this.#controller?.setUiLocale(locale);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.#controller || oldValue === newValue) return;
    try {
      switch (name) {
        case "theme":
          this.#controller.setTheme(parseThemeAttribute(newValue) ?? "auto");
          break;
        case "accent-color":
          this.#controller.setAccentColor(parseAccentColorAttribute(newValue));
          break;
        case "orb-palette":
          this.#controller.setOrbPalette(parseOrbPaletteAttribute(newValue));
          break;
        case "lang-strings":
          this.#controller.setLangStrings(parseLangStringsAttribute(newValue));
          break;
        case "ui-locale": {
          const locale = parseUiLocaleAttribute(newValue);
          // `ui-locale` and `lang-strings` are both supported at once, with
          // `ui-locale.strings` taking precedence. When `ui-locale` is removed
          // (newValue === null) its reset payload clears `strings`, but a valid
          // `lang-strings` may still be present — recompute the strings override
          // from that remaining attribute instead of falling back to English.
          if (newValue === null) {
            const rawLangStrings = this.getAttribute("lang-strings");
            locale.strings = rawLangStrings?.trim()
              ? parseLangStringsAttribute(rawLangStrings)
              : undefined;
          }
          this.#controller.setUiLocale(locale);
          break;
        }
      }
    } catch (e) {
      console.error(`[voice-agent] failed to update ${name}:`, e);
    }
  }
}

/** Define `<voice-agent>` (idempotent; safe to call more than once). */
export function register(): void {
  if (typeof customElements !== "undefined" && !customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, VoiceAgentElement);
  }
}
